import { Controller } from "@hotwired/stimulus";
import { detectBrowserMetadata } from "../../../utils/browserMetadataHandler";
import { debugLog, debugError, formatTimer } from "../../../utils";
import { otpInputDigits } from "./otpInput";

export default class OtpController extends Controller {
  static targets = [
    "password",
    "timer",
    "expirationCounter",
    "resendButton",
    "invalidPasswordError",
    "internalServerError",
  ];

  static values = {
    code: String,
    session: String,
    email: String,
    maskedEmail: String,
    resendIntervalInSeconds: { type: Number, default: 60 },
    /**
     * Where to POST the OTP form:
     * - "verify-otp" → POST /verify-otp/ (DFNS / Coinsub wallet email verification — handleOTPVerification)
     * - otherwise → POST /:code/{value}?session=… (e.g. fast-track-otp-verification, not-enough-crypto-otp-verification)
     */
    verificationEndpoint: { type: String, default: "verify-otp" },
    /** fast-track: resend via verify-otp/resend. not-enough-crypto: reload OTP page to resend email. */
    resendMode: { type: String, default: "fast-track" },
  };

  connect() {
    document.getElementById("email_holder").textContent = this.maskedEmailValue;
    this.startResendOTPCountdown();
    this.hideLoadingScreen();
  }

  startResendOTPCountdown() {
    let remainingSeconds = this.resendIntervalInSecondsValue;

    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }

    this.expirationCounterTarget.style.display = "inline";
    this.resendButtonTarget.disabled = true;
    this.resendButtonTarget.textContent = "";

    // Update the timer immediately
    this.timerTarget.textContent = formatTimer(remainingSeconds);

    this.timerInterval = setInterval(() => {
      remainingSeconds -= 1;
      this.timerTarget.textContent = formatTimer(remainingSeconds);

      if (remainingSeconds === 0) {
        clearInterval(this.timerInterval);
        this.expirationCounterTarget.style.display = "none";
        this.resendButtonTarget.disabled = false;
        this.resendButtonTarget.textContent = "Resend";
      }
    }, 1000);
  }

  async resendPassword() {
    if (this.resendModeValue === "not-enough-crypto") {
      Turbo.visit(
        `/${this.codeValue}/not-enough-crypto-otp?session=${encodeURIComponent(this.sessionValue)}`,
      );
      return;
    }

    this.showLoadingScreen();
    try {
      const request = await fetch("/verify-otp/resend/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Session": this.sessionValue,
          "X-Code": this.codeValue,
        },
        body: JSON.stringify({
          wallet_type: "DFNS",
        }),
      });

      if (!request.ok) {
        // Catalog envelope (OTP-07): render the server's code-not-sent card
        // instead of collapsing to the generic auto-hide error.
        const errorBody = await request.json().catch(() => ({}));
        if (errorBody.error_html) {
          Turbo.renderStreamMessage(errorBody.error_html);
          return;
        }
        throw new Error(`Resend OTP failed: ${request.status}`);
      }

      await request.json();
      this.startResendOTPCountdown();
    } catch (error) {
      debugError("Error resending OTP:", error);
      this.showInternalServerError();
    } finally {
      this.hideLoadingScreen();
    }
  }

  handlePasswordInput(passwordInput) {
    const trimmedInput = passwordInput.trim();
    const numericInput = trimmedInput.replace(/\D/g, "").slice(0, 6);

    this.handleOTPInput(numericInput);
  }

  async handleOTPInput(password) {
    this.showLoadingScreen();

    const metadata = detectBrowserMetadata();
    metadata.appType = "buy";
    const jsonMetadata = JSON.stringify(metadata);

    try {
      const formData = new FormData();
      formData.append("form_email", this.emailValue);
      formData.append("form_password", password);
      formData.append("form_metadata", jsonMetadata);

      const verifyURL =
        this.verificationEndpointValue === "verify-otp"
          ? `/verify-otp/?session=${encodeURIComponent(this.sessionValue)}`
          : `/${this.codeValue}/${this.verificationEndpointValue}?session=${encodeURIComponent(this.sessionValue)}`;

      const response = await fetch(verifyURL, {
        method: "POST",
        headers: {
          "X-Session": this.sessionValue,
          "X-Code": this.codeValue,
          "Turbo-Frame": "step_content",
        },
        body: formData,
        redirect: "follow",
      });

      if (!response.ok) {
        // Handle error response
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { error: "An error occurred" };
        }
        
        this.hideLoadingScreen();
        
        if (response.status === 400) {
          if (errorData.error_key) {
            this.showCatalogError(errorData.title || "", errorData.message || "");
          } else {
            debugError("Unexpected error message from BE. Showing the standard error message.")
            this.showInvalidPasswordError();
          }
        } else {
          this.showInternalServerError();
        }
        return;
      }

      // Success - handle redirect or HTML response
      if (response.redirected) {
        // Backend redirected (e.g., to passkey page or payment-options)
        // Use Turbo to navigate to maintain Turbo's navigation state
        // Fast track: replace the spent OTP step in history so browser back
        // lands on the email step, same as the pay page's back arrow.
        if (this.verificationEndpointValue === "fast-track-otp-verification") {
          Turbo.visit(response.url, { action: "replace" });
        } else {
          Turbo.visit(response.url);
        }
        return;
      }

      const contentType = response.headers.get("Content-Type") || "";
      
      if (contentType.includes("text/html")) {
        // HTML/Turbo Stream response
        const html = await response.text();
        Turbo.renderStreamMessage(html);
        this.hideLoadingScreen();
      } else {
        // JSON response
        const data = await response.json();
        if (data.type === "html" && data.data) {
          Turbo.renderStreamMessage(data.data);
        }
        this.hideLoadingScreen();
      }
    } catch (error) {
      debugError("Error in handleOTPInput:", error);
      this.hideLoadingScreen();
      this.showInternalServerError();
    }
  }

  pasteFromClipboard() {
    navigator.clipboard.readText().then((passwordInput) => {
      // Trim whitespace and filter out non-numeric characters, then take first 6 digits
      const trimmedInput = passwordInput.trim();
      const numericInput = trimmedInput.replace(/\D/g, "").slice(0, 6);

      this.passwordTargets.forEach((passwordTarget, index) => {
        passwordTarget.value = numericInput[index] || "";
      });

      // If we have a complete OTP, trigger verification
      if (numericInput.length === 6) {
        this.handlePasswordInput(numericInput);
      }
    });
  }

  handleOTPInputChange(e) {
    // A fresh attempt dismisses the persistent catalog error.
    this.invalidPasswordErrorTarget.classList.add("hidden");

    const { code, digit } = otpInputDigits(e.target.value, this.passwordTargets.length);
    if (code) {
      this.passwordTargets.forEach((passwordTarget, index) => {
        passwordTarget.value = code[index];
      });
      this.passwordTargets[this.passwordTargets.length - 1].focus();
      this.handlePasswordInput(code);
      return;
    }

    // This will prevent any non-numeric characters.
    e.target.value = digit;
    if (!digit) {
      return;
    }

    // allow user to enter the next input without clicking
    if (e.target.value.length === 1) {
      if (e.target.nextElementSibling) {
        e.target.nextElementSibling.focus();
      }
    }

    // Check if all 6 digits are entered after any input change
    const allDigitsEntered = this.passwordTargets.every(
      (password) => password.value.length === 1,
    );

    if (allDigitsEntered) {
      const passwordInput = this.passwordTargets
        .map((code) => code.value)
        .join("");
      this.handlePasswordInput(passwordInput);
    }
  }

  handleOTPInputKeyDown(e) {
    if (
      (e.key === "Backspace" || e.key === "Delete") &&
      e.target.value === ""
    ) {
      // Move focus to previous input if exists
      if (e.target.previousElementSibling) {
        e.target.previousElementSibling.focus();
      }
    }
  }

  handleOTPInputPaste(e) {
    e.preventDefault(); // Prevent default paste behavior
    this.pasteFromClipboard();
  }

  // Renders a catalog inline-field error (title bold, body below). Unlike the
  // legacy messages this does NOT auto-hide: a lockout that vanishes after 5s
  // sends the customer back to retrying a code that cannot work. It clears
  // when the customer starts typing again.
  showCatalogError(title, message) {
    const container = this.invalidPasswordErrorTarget;
    container.textContent = "";
    if (title) {
      const titleElement = document.createElement("div");
      titleElement.className = "font-semibold";
      titleElement.textContent = title;
      container.appendChild(titleElement);
    }
    const bodyElement = document.createElement("div");
    bodyElement.textContent = message;
    container.appendChild(bodyElement);
    container.classList.remove("hidden");

    this.passwordTargets.forEach((passwordTarget) => {
      passwordTarget.value = "";
    });
    if (this.passwordTargets.length > 0) {
      this.passwordTargets[0].focus();
    }
  }

  showInvalidPasswordError() {
    this.invalidPasswordErrorTarget.classList.remove("hidden");

    // Clear all OTP input fields
    this.passwordTargets.forEach((passwordTarget) => {
      passwordTarget.value = "";
    });

    // Focus on the first input field for better UX
    if (this.passwordTargets.length > 0) {
      this.passwordTargets[0].focus();
    }

    setTimeout(() => {
      this.invalidPasswordErrorTarget.classList.add("hidden");
    }, 5000);
  }

  showInternalServerError() {
    this.internalServerErrorTarget.classList.remove("hidden");

    // Clear all OTP input fields
    this.passwordTargets.forEach((passwordTarget) => {
      passwordTarget.value = "";
    });

    // Focus on the first input field for better UX
    if (this.passwordTargets.length > 0) {
      this.passwordTargets[0].focus();
    }

    setTimeout(() => {
      this.internalServerErrorTarget.classList.add("hidden");
    }, 5000);
  }

  showLoadingScreen() {
    this.dispatch("showLoadingScreen", { detail: { text: "Please Wait" } });
  }

  hideLoadingScreen() {
    this.dispatch("hideLoadingScreen");
  }
}
